import first from "lodash/first";
import isEmpty from "lodash/isEmpty";

import type { Member, Sender } from "@/crossPlatform/Sendbird";
import type { AvatarCardProps } from "@/ui/core/AvatarCard";
import { getProviderDisplayName } from "@/utils/groupChannel.util";
import { joinWithCommas } from "@/utils/string.util";

const sendbirdUserTypeToUserType = (
  sendbirdUserType: SendbirdUserType,
): BaseUserType => {
  switch (sendbirdUserType) {
    case "Patient":
      return "patient";
    case "Provider":
      return "provider";
    case "Support":
      return "support";
  }
};

const sendbirdMemberToUserAvatar = (member: Member) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Sendbird defines member.metaData as a generic 'object' type; our MemberMetaData type is more specific
  const metadata = member.metaData as MemberMetaData;

  const baseAvatar: BaseUserAvatarProps = {
    imageUrl: member.profileUrl,
    userId: member.userId,
    userName: member.nickname,
    userType: sendbirdUserTypeToUserType(metadata.userType),
  };

  return baseAvatar;
};

const sendbirdSenderToAvatarCard = (sender: Sender) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Sendbird defines member.metaData as a generic 'object' type; our MemberMetaData type is more specific
  const metadata = sender.metaData as MemberMetaData;

  const providerType = getProviderDisplayName(metadata.providerType);

  const normalizedSubtitle = isEmpty(providerType) ? undefined : providerType;

  const avatarCard: AvatarCardProps = {
    imageUrl: sender.profileUrl,
    subtitle: normalizedSubtitle,
    userId: sender.userId,
    userName: sender.nickname,
    userType: sendbirdUserTypeToUserType(metadata.userType),
  };

  return avatarCard;
};

const sendbirdMemberToAvatarCard = (member: Member) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Sendbird defines member.metaData as a generic 'object' type; our MemberMetaData type is more specific
  const metadata = member.metaData as MemberMetaData;

  const providerType = getProviderDisplayName(metadata.providerType);

  const normalizedSubtitle = isEmpty(providerType) ? undefined : providerType;

  const avatarCard: AvatarCardProps = {
    imageUrl: member.profileUrl,
    subtitle: normalizedSubtitle,
    userId: member.userId,
    userName: member.nickname,
    userType: sendbirdUserTypeToUserType(metadata.userType),
  };

  return avatarCard;
};

const getProviderNames = (members: Member[]) => {
  const providers = members
    .filter((member) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Sendbird defines member.metaData as a generic 'object' type; our MemberMetaData type is more specific
      const metadata = member.metaData as MemberMetaData;
      return metadata.userType === "Provider";
    })
    .map((member) => member.nickname);

  return joinWithCommas(providers);
};

/**
 * Extracts the document ID from the filename of a patient document attachment shared in a chat via Sendbird.
 *
 * When a chat attachment is uploaded, a UUID is appended to its filename to identify the object ID
 * in Sendbird's fileMessage, as we can't manipulate metadata. This function matches the UUID regex
 * to obtain that file ID in order to perform additional operations or queries in the database.
 *
 * @param filename - The name of the file from which to extract the UUID.
 * @returns The extracted UUID if found, otherwise an empty string.
 */
const getPatientDocumentExternalIdFromFileName = (filename: string) => {
  if (isEmpty(filename)) {
    return "";
  }

  const result = filename.match(
    /[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/gm,
  );

  const firstMatch = first(result);

  return firstMatch || "";
};

const removeUUIDFromFilename = (filename: string): string => {
  if (isEmpty(filename)) {
    return filename;
  }

  const uuidPattern = /[a-f0-9]{8}-([a-f0-9]{4}-){3}[a-f0-9]{12}-/;
  // eslint-disable-next-line sonarjs/prefer-regexp-exec -- currently reported in SonarQube as unresolved
  const match = filename.match(uuidPattern);

  if (match) {
    // Replace UUID with an empty string
    return filename.replace(match[0], "");
  }

  // If not enough parts or no UUID found, keep the original filename
  return filename;
};

const getMessageDirection = ({
  appUserId,
  messageSender: sender,
}: {
  appUserId: string;
  messageSender: Sender;
}): ChatMessageDirection => {
  const isOutgoing = sender.userId === appUserId;
  return isOutgoing ? "outgoing" : "incoming";
};

const shouldDisableSendButton = ({
  isLoading,
  message,
  selectedAttachment,
}: {
  isLoading: boolean;
  message?: string;
  selectedAttachment?: FilePickerAsset | null;
}) => {
  return isLoading || (isEmpty(message) && !selectedAttachment);
};

export {
  getMessageDirection,
  getPatientDocumentExternalIdFromFileName,
  getProviderNames,
  removeUUIDFromFilename,
  sendbirdMemberToAvatarCard,
  sendbirdMemberToUserAvatar,
  sendbirdSenderToAvatarCard,
  sendbirdUserTypeToUserType,
  shouldDisableSendButton,
};
